import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { ConciergeProvider } from "@/context/ConciergeContext";
import Layout from "@/components/Layout";
import ConsentBanner from "@/components/ConsentBanner";
import { initAnalytics, pageview } from "@/lib/analytics";
import { AskSpotterProvider } from "@/context/AskSpotterContext";
import Landing from "@/pages/Landing";
import Shop from "@/pages/Shop";
import Collections from "@/pages/Collections";
import ProductDetail from "@/pages/ProductDetail";
import VendorPage from "@/pages/VendorPage";
import VendorsIndex from "@/pages/VendorsIndex";
import Auth from "@/pages/Auth";
import Cart from "@/pages/Cart";
import BuyerDashboard from "@/pages/BuyerDashboard";
import MyAccount from "@/pages/MyAccount";
import SavedAddresses from "@/pages/SavedAddresses";
import Wishlist from "@/pages/Wishlist";
import StoreCredit from "@/pages/StoreCredit";
import HelpSupport from "@/pages/HelpSupport";
import Legal from "@/pages/Legal";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import SizeMatcher from "@/pages/SizeMatcher";
import Process from "@/pages/Process";
import Spotters from "@/pages/Spotters";
import Journal from "@/pages/Journal";
import Manifesto from "@/pages/Manifesto";
import Sell from "@/pages/Sell";
import BecomeVendor from "@/pages/BecomeVendor";
import VendorRegister from "@/pages/VendorRegister";
import OrderConfirmed from "@/pages/OrderConfirmed";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";
import VendorLayout from "@/pages/vendor/VendorLayout";
import VendorOverview from "@/pages/vendor/VendorOverview";
import VendorProducts from "@/pages/vendor/VendorProducts";
import VendorOrders from "@/pages/vendor/VendorOrders";
import VendorAnalytics from "@/pages/vendor/VendorAnalytics";
import VendorPayouts from "@/pages/vendor/VendorPayouts";
import VendorAgreement from "@/pages/vendor/VendorAgreement";
import VendorProfile from "@/pages/vendor/VendorProfile";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-24 font-mono text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Reset scroll to the top on every route change (SPA navigation doesn't do this)
// and record a GA4 page_view for the new path.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); pageview(pathname); }, [pathname]);
  return null;
}

function AppInner() {
  const inCallback = typeof window !== "undefined"
    && (window.location.hash || "").includes("session_id=");
  useEffect(() => { initAnalytics(); }, []);
  if (inCallback) return <AuthCallback />;
  return (
    <AskSpotterProvider>
    <ScrollToTop />
    <ConsentBanner />
    <Layout>
      <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/vendors" element={<VendorsIndex />} />
                <Route path="/vendor/:slug" element={<VendorPage />} />
                <Route path="/size-match" element={<SizeMatcher />} />
                <Route path="/process" element={<Process />} />
                <Route path="/login" element={<Auth mode="login" />} />
                <Route path="/register" element={<Auth mode="register" />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/account" element={<Protected roles={["buyer"]}><BuyerDashboard /></Protected>} />
                <Route path="/account/profile" element={<Protected roles={["buyer"]}><MyAccount /></Protected>} />
                <Route path="/account/addresses" element={<Protected roles={["buyer"]}><SavedAddresses /></Protected>} />
                <Route path="/account/wishlist" element={<Protected roles={["buyer"]}><Wishlist /></Protected>} />
                <Route path="/account/credit" element={<Protected roles={["buyer"]}><StoreCredit /></Protected>} />
                <Route path="/help" element={<HelpSupport />} />
                <Route path="/legal/:slug" element={<Legal />} />
                <Route path="/account/security" element={<Protected><ChangePassword /></Protected>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/vendor" element={<Protected roles={["vendor"]}><VendorLayout /></Protected>}>
                  <Route index element={<VendorOverview />} />
                  <Route path="products" element={<VendorProducts />} />
                  <Route path="orders" element={<VendorOrders />} />
                  <Route path="analytics" element={<VendorAnalytics />} />
                  <Route path="payouts" element={<VendorPayouts />} />
                  <Route path="profile" element={<VendorProfile />} />
                  <Route path="agreement" element={<VendorAgreement />} />
                </Route>
                <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
                <Route path="/spotters" element={<Spotters />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/manifesto" element={<Manifesto />} />
                <Route path="/sell" element={<Sell />} />
                <Route path="/become-a-vendor" element={<BecomeVendor />} />
                <Route path="/vendor-register" element={<VendorRegister />} />
                <Route path="/order-confirmed/:id" element={<Protected><OrderConfirmed /></Protected>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
    </AskSpotterProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <ConciergeProvider>
                <AppInner />
              </ConciergeProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
